import { t } from 'i18next';
import {
  HiArrowSmallUp,
  HiOutlineArrowSmallDown,
  HiOutlineDocumentText,
  HiOutlineChevronLeft,
  HiOutlineChevronRight
} from 'react-icons/hi2';

import { renderCellContent } from './RenderCellContent';

export interface TableColumn<T> {
  columnKey: keyof T;
  header: string;
}

interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  itemsPerPage: number;
  totalItems: number;
  toNextPage: number;
  currentPage: number;
  onPageChange: (page: number, sortBy: string, sortDirection: string) => void;
  sortBy: string;
  sortDirection: string;
}

export const DynamicTable = <T extends Record<string, any>>({
  data,
  columns,
  itemsPerPage,
  totalItems,
  currentPage = 1,
  toNextPage,
  onPageChange,
  sortBy,
  sortDirection
}: TableProps<T>) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleSort = (columnKey: keyof T) => {
    // Toggle: same column flips direction; first click on a new column defaults to desc
    // (newest-first for dates / high-to-low for ids), matching the registrar home default.
    const newSortDirection =
      sortBy === columnKey ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'desc';
    onPageChange(currentPage, columnKey as string, newSortDirection);
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="w-full">
        <div className="hidden md:block">
          {/* Tabela para telas médias e grandes */}
          <table className="w-full border-collapse">
            <thead className="bg-[#F8F9FA]">
              <tr
                className="grid w-full border-b-[2px] border-[#E1E9EF] py-4"
                style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
              >
                {columns.map((column) => (
                  <th
                    className="cursor-pointer px-4 text-center text-sm font-bold text-[#0C1FAA] transition-colors hover:text-[#0A1888]"
                    key={String(column.columnKey)}
                    onClick={() => handleSort(column.columnKey)}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {column.header}
                      {sortBy === column.columnKey && (
                        <span className="inline-flex items-center">
                          {sortDirection === 'asc' ? <HiArrowSmallUp size={18} /> : <HiOutlineArrowSmallDown size={18} />}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, rowIndex) => (
                <tr
                  className="grid w-full border-b border-[#E1E9EF] transition-colors hover:bg-[#F8F9FA]"
                  key={rowIndex}
                  style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
                >
                  {columns.map((column) => (
                    <td className="px-4 py-4 text-center text-sm text-[#373D43]" key={String(column.columnKey)}>
                      {renderCellContent(column.columnKey, item)}
                    </td>
                  ))}
                </tr>
              ))}
              {data.length === 0 && (
                <tr className="grid w-full">
                  <td className="col-span-full py-16 text-center">
                    <HiOutlineDocumentText className="m-auto mb-3 text-[#727272]" size={48} />
                    <p className="text-base font-semibold text-[#0C1FAA]">{t('labels.lblNoDataAvailable')}</p>
                    <p className="mt-1 text-sm text-[#727272]">Nenhum registro encontrado</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Formato de cartões para telas pequenas */}
        <div className="space-y-3 p-4 md:hidden">
          {data.map((item, rowIndex) => (
            <div key={rowIndex} className="rounded-lg border-[2px] border-solid border-[#E1E9EF] bg-white p-4 transition-all hover:border-[#0C1FAA]">
              {columns.map((column) => (
                <div key={String(column.columnKey)} className="flex justify-between border-b border-[#E1E9EF] py-3 last:border-b-0">
                  <span className="font-semibold text-[#0C1FAA]">{column.header}:</span>
                  <span className="text-[#373D43]">{renderCellContent(column.columnKey, item)}</span>
                </div>
              ))}
            </div>
          ))}
          {data.length === 0 && (
            <div className="py-16 text-center">
              <HiOutlineDocumentText className="m-auto mb-3 text-[#727272]" size={48} />
              <p className="text-base font-semibold text-[#0C1FAA]">{t('labels.lblNoDataAvailable')}</p>
              <p className="mt-1 text-sm text-[#727272]">Nenhum registro encontrado</p>
            </div>
          )}
        </div>

        {/* Paginação */}
        <div className="flex w-full flex-col items-center gap-4 border-t-[2px] border-[#E1E9EF] bg-[#F8F9FA] px-6 py-4 md:flex-row md:justify-between">
          <div className="text-sm text-[#727272]">
            {t('others.othRegisterOfNumbers')}: <span className="font-semibold text-[#0C1FAA]">{totalItems}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="flex items-center gap-2 rounded-lg border-[2px] border-solid border-[#E1E9EF] bg-white px-4 py-2 text-sm font-medium text-[#0C1FAA] transition-all hover:border-[#0C1FAA] hover:bg-[#0C1FAA] hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[#E1E9EF] disabled:hover:bg-white disabled:hover:text-[#0C1FAA]"
              onClick={() => onPageChange(currentPage - 1, sortBy, sortDirection)}
              disabled={currentPage === 1 || totalPages <= 1}
            >
              <HiOutlineChevronLeft size={18} />
              {t('labels.lblPevPage')}
            </button>
            <span className="text-sm text-[#373D43]">
              {t('labels.lblPage')} <span className="font-semibold text-[#0C1FAA]">{currentPage}</span> {t('others.othOfTable')} <span className="font-semibold text-[#0C1FAA]">{totalPages}</span>
            </span>
            <button
              className="flex items-center gap-2 rounded-lg border-[2px] border-solid border-[#E1E9EF] bg-white px-4 py-2 text-sm font-medium text-[#0C1FAA] transition-all hover:border-[#0C1FAA] hover:bg-[#0C1FAA] hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[#E1E9EF] disabled:hover:bg-white disabled:hover:text-[#0C1FAA]"
              onClick={() => onPageChange(currentPage + 1, sortBy, sortDirection)}
              disabled={currentPage === totalPages}
            >
              {t('labels.lblNextPage')}
              <HiOutlineChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
